import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { Reveal } from "../components/Reveal";
import { imgUrl } from "../lib/spa";
import { Seo } from "../components/Seo";

const FALLBACK_SPACE = [
  "/api/files/magnolia-lunar/cdn/massages/massagem-tantrica/cover-1773411888989.jpg",
  "/api/files/magnolia-lunar/cdn/massages/massagem-relaxante/cover-1773411247527.jpg",
  "/api/files/magnolia-lunar/cdn/massages/massagem-nuru/cover-1773411491601.jpg",
];

export default function About() {
  const { t } = useLanguage();
  const { therapists, spacePhotos } = useSpaData();
  const SPACE = (spacePhotos && spacePhotos.length > 0)
    ? spacePhotos.map((p) => p.url)
    : FALLBACK_SPACE;

  return (
    <div data-testid="about-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
      <Seo title={t("seo.about.title")} description={t("seo.about.desc")} />
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("about.label")}</p>
        <h1 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl lg:text-6xl">{t("about.title")}</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/72">{t("about.subtitle")}</p>
      </Reveal>

      {/* Gallery */}
      <div className="mt-14 grid gap-4 sm:grid-cols-3">
        {SPACE.map((img, i) => (
          <Reveal key={img} delay={i * 0.1}>
            <div className={`group overflow-hidden rounded-2xl border border-foreground/8 ${i === 0 ? "sm:row-span-2" : ""}`}>
              <img
                src={imgUrl(img)}
                alt={`Espaço Magnolia Lunar ${i + 1}`}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:aspect-auto sm:h-full"
              />
            </div>
          </Reveal>
        ))}
      </div>

      {/* How it works */}
      <div className="mt-28">
        <Reveal>
          <h2 className="text-center font-display text-4xl font-light text-foreground sm:text-5xl">{t("about.howTitle")}</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {t("about.steps").map((step, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="relative h-full rounded-2xl border border-foreground/8 bg-card p-8">
                <span className="font-display text-5xl font-light text-gold/30">0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl font-medium text-foreground">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/72">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Therapists strip */}
      <div className="mt-28">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("team.label")}</p>
              <h2 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl">{t("about.chooseTherapist")}</h2>
              <p className="mt-4 max-w-lg text-sm text-foreground/72">{t("about.chooseTherapistDesc")}</p>
            </div>
            <Link
              data-testid="about-all-therapists-link"
              to="/terapeutas"
              className="group inline-flex shrink-0 items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold"
            >
              {t("team.viewAll")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-3 gap-4 sm:grid-cols-6">
          {therapists.map((th, i) => (
            <Reveal key={th.slug} delay={(i % 6) * 0.06}>
              <Link to={`/terapeutas/${th.slug}`} data-testid={`about-therapist-${th.slug}`} className="group block text-center">
                <div className="overflow-hidden rounded-xl border border-foreground/10 transition-colors duration-300 group-hover:border-gold/50">
                  <img
                    src={imgUrl(th.profile)}
                    alt={th.name}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <span className="mt-2 block text-sm text-foreground/82 transition-colors duration-300 group-hover:text-gold">
                  {th.name}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
